import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { BottleneckFlag, Card, CardContent, CardHeader, CardTitle, Tabs, TabsContent, TabsList, TabsTrigger } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { useProcessDetail } from '../hooks/useProcessDetail';
import { useProcessenFilter } from '../hooks/useProcessenFilter';
import { hasTimingOrListUnreachableFilter, hasPeriodOrStatusFilter, PROCESS_FILTER_VELDEN } from '../procesLijsten';
import { DqsEnrichmentBlock } from '../components/DqsEnrichmentBlock';
import { KpiSummaryBlock } from '../components/KpiSummaryBlock';
import { formatPercent, formatWorkdays, hasNoProcessData } from '../format';
import { FilterRejectedMessage } from '@/components/feedback/FilterRejectedMessage';
import { isProcessFilterRejected, PROCESS_FILTER_TEKSTEN, ProcessFilters, processFilterTestId } from '../components/ProcessFilters';
import { ProcesStappenbalk } from '../components/ProcesStappenbalk';
import { ProductenPerProces } from '../components/ProductenPerProces';
import { GebeurtenissenStroom } from '../components/GebeurtenissenStroom';
import { StepTransitionTimingBlock } from '../components/StepTransitionTimingBlock';
import { formatFetchedAt } from '../format';
import { CANONICAL_PROCESS_NAMES, SERVICE_CODE } from '../processes.constants';
import type { CanonicalProcessName, CoverageState, DienstProcessName } from '../processes.api';

/** Tabblad-sleutels; techniek, geen zichtbare tekst. */
const TAB = { producten: 'tab-producten', stroom: 'tab-stroom' } as const;

/** Kleuren van een kerncijfer; opmaakklassen, geen zichtbare tekst. */
const CIJFER_TONE = {
  neutraal: 'text-[var(--foreground)]',
  primair: 'text-[var(--primary)]',
  alarm: 'text-[var(--destructive)]',
} as const;

/** De productenlijst van het fysieke proces; een route, geen zichtbare tekst. */
const PHYSICAL_ITEMS_PATH = '/processes/in-huis';

/** De dienstcode van het fysieke proces; die staat niet in SERVICE_CODE, dat de acht diensten dekt. */
const PHYSICAL_SERVICE_CODE = 'PHYSICAL';

/** Eén van de vier cijfers bovenaan het scherm, zoals de mockup ze naast elkaar zet. */
function KernCijfer({ label, value, tone, testId }: { label: string; value: string; tone?: string; testId: string }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background)] p-3.5" data-testid={testId}>
      <p className={`text-2xl font-bold tracking-tight ${tone ?? CIJFER_TONE.neutraal}`}>{value}</p>
      <p className="mt-1 text-xs text-[var(--foreground-muted)]">{label}</p>
    </div>
  );
}

/** Dezelfde drie staten als op de kaart in het overzicht: gevoed · geen data · bron offline. */
function CoverageBadge({ coverageState, sourceUnavailable, testId }: { coverageState: CoverageState; sourceUnavailable: boolean; testId: string }) {
  const { t } = useTranslation();
  const state = sourceUnavailable ? 'offline' : hasNoProcessData(coverageState) ? 'noData' : 'fed';
  const tone =
    state === 'fed'
      ? 'border-[var(--success-dark)] bg-[var(--success-light)] text-[var(--success-dark)]'
      : state === 'offline'
        ? 'border-[var(--destructive)] bg-[var(--destructive-light)] text-[var(--destructive)]'
        : 'border-[var(--border)] bg-[var(--background-muted)] text-[var(--foreground-muted)]';
  return <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone}`} data-testid={testId}>{t(`processes.card.badge.${state}`)}</span>;
}

function isCanonicalProcessName(value: string | undefined): value is CanonicalProcessName {
  return value != null && (CANONICAL_PROCESS_NAMES as readonly string[]).includes(value);
}

/** Story 3.4 — de route naar het stap-detailscherm (taal-neutrale technische padopbouw, geen user-facing copy). */
function buildStepDetailPath(name: CanonicalProcessName, step: string): string {
  return `/processes/${name}/steps/${encodeURIComponent(step)}`;
}

/**
 * spec-processen-actuele-stand (CAP-4): een fysieke stap opent de productenlijst met stapfilter (`?step=`,
 * startfilter van de stapkolom). Klant en drempel volgen via de gedeelde Processen-filter (story 12).
 */
function buildPhysicalStepSearch(step: string): string {
  return new URLSearchParams({ step }).toString();
}

/** Het procesdetail heeft geen eigen lijst die het adres leest (producten per proces leest het niet, story 8). */
const GEEN_EIGEN_LIJSTEN = [] as const;

export function ProcesDetail() {
  const { t, i18n } = useTranslation();
  const kopRef = useRef<HTMLHeadingElement>(null);
  const navigate = useNavigate();
  const params = useParams<{ name: string }>();
  const name = isCanonicalProcessName(params.name) ? params.name : null;
  const { values: filterValues, setFilters, wis, geweigerd, defaultStilstandDrempelWerkdagen } = useProcessenFilter(GEEN_EIGEN_LIJSTEN);
  const [tab, setTab] = useState<string>(TAB.producten);
  const detailQuery = useProcessDetail(name, filterValues);

  if (name == null) {
    return (
      <p className="text-sm text-[var(--foreground-muted)]" data-testid="process-detail-unknown-name" role="alert">
        {t('errors.notFound')}
      </p>
    );
  }

  // CAP-2: een geweigerd filter keert niet terug vóór de filterbalk — die is de weg terug.
  const filterRejected = isProcessFilterRejected(detailQuery.error, detailQuery.data?.data.sources.procesManagement);
  if (detailQuery.isError && !filterRejected) {
    return <ApiErrorMessage error={detailQuery.error} onRetry={() => detailQuery.refetch()} />;
  }

  const data = filterRejected ? undefined : detailQuery.data?.data;
  const processLabel = t(`processes.names.${name}`);
  // Story 4 (spec-processen-klantfilter): the step timings and the service list are not reached by an active filter. A detail without process data draws no counts, so no note.
  const allCustomers = data != null && !hasNoProcessData(data.coverageState) && hasTimingOrListUnreachableFilter(filterValues);
  const listFilterNotApplied = !allCustomers ? undefined : name !== 'physical' ? 'all' : hasPeriodOrStatusFilter(filterValues) ? 'periodStatus' : undefined;

  return (
    <div className="space-y-5" data-testid="process-detail-page">
      {/* Story 3 (CAP-5): de weg terug houdt de filters vast, via de gedeelde lijststand (story 12). */}
      <Link className="text-xs text-[var(--secondary)] hover:underline" data-testid="process-detail-back" to="/processes">
        {t('processes.detail.backToOverview')}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 ref={kopRef} tabIndex={-1} className="text-xl font-bold tracking-tight text-[var(--foreground)]">{processLabel}</h1>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--foreground-muted)]">{SERVICE_CODE[name as DienstProcessName] ?? PHYSICAL_SERVICE_CODE}</p>
          {data ? (
            <p aria-live="polite" className="text-xs text-[var(--foreground-muted)]" data-testid="process-detail-updated-at">
              {t('processes.updatedAt', { time: formatFetchedAt(data.fetchedAt, i18n.language) })}
            </p>
          ) : null}
        </div>
        {data ? <CoverageBadge coverageState={data.coverageState} sourceUnavailable={data.sources.procesManagement === 'unavailable'} testId="process-detail-badge" /> : null}
      </header>

      <ProcessFilters defaultStilstandDrempelWerkdagen={defaultStilstandDrempelWerkdagen} onChange={setFilters} values={filterValues} />

      <FilterRejectedMessage error={detailQuery.error} focusNaWissen={kopRef} linkGeweigerd={geweigerd} onClear={wis} rejected={filterRejected} teksten={PROCESS_FILTER_TEKSTEN} baseTestId={processFilterTestId} velden={PROCESS_FILTER_VELDEN} />

      {detailQuery.isLoading ? <div className="h-48 animate-pulse rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background-muted)]" data-testid="process-detail-loading" /> : null}

      {data ? (
        <>
          {data.sources.procesManagement === 'unavailable' ? (
            <p className="text-xs text-[var(--info-text)]" role="status">{t('processes.degraded.title')}</p>
          ) : null}

          {/* Story 6.1 (FR15/FR16): DQS-QA-verrijking is uitsluitend relevant voor de
              meet-/QA-subprocessen. Zelfde procesafbakening als het fysieke doelbeeld (AD-17,
              PHYSICAL_TARGET_SUBPROCESS_KEYS): physical + packshot, NIET aanmelding (dat is een
              administratief proces zonder meet-/QA-component). */}
          {name === 'physical' || name === 'packshot' ? <DqsEnrichmentBlock status={data.sources.dqs} /> : null}

          {/* CAP-8: fysiek en de bron onbereikbaar — geen getallen, de melding hierboven zegt genoeg. */}
          {name === 'physical' && data.sources.procesManagement === 'unavailable' ? null : (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="process-detail-kerncijfers">
              <KernCijfer label={t('processes.card.openProductsLabel')} testId="kerncijfer-open" value={String(data.openProductCount)} />
              <KernCijfer
                label={t('processes.card.leadTime')}
                testId="kerncijfer-doorlooptijd"
                tone={CIJFER_TONE.primair}
                value={data.kpiSummary.meanWorkdays != null ? formatWorkdays(data.kpiSummary.meanWorkdays) : t('processes.placeholder.notYetAvailable')}
              />
              <KernCijfer
                label={t('processes.card.kpiWithinTarget')}
                testId="kerncijfer-kpi"
                value={data.kpiSummary.pctWithinKpi != null ? formatPercent(data.kpiSummary.pctWithinKpi) : t('common.notAvailable')}
              />
              <KernCijfer label={t('processes.card.stagnation')} testId="kerncijfer-stilstand" tone={data.stagnation.count > 0 ? CIJFER_TONE.alarm : undefined} value={String(data.stagnation.count)} />
            </div>
          )}

          {/* Story 7 (CAP-10): not all orders of this service were fetched. */}
          {data.ordersTruncated ? (
            <p className="text-[11px] text-[var(--info-text)]" data-testid="process-detail-orders-truncated">{t('processes.ordersTruncated.dienst')}</p>
          ) : null}

          {/* Story 4: the physical counts follow customer and threshold, not period or status. */}
          {name === 'physical' && hasPeriodOrStatusFilter(filterValues) && !hasNoProcessData(data.coverageState) ? (
            <p className="text-[11px] text-[var(--info-text)]" data-testid="process-detail-period-status">{t('processes.allCustomers.physicalCounts')}</p>
          ) : null}

          <Card data-testid="process-detail-funnel">
            <CardHeader>
              <CardTitle>{t('processes.detail.funnelTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              {hasNoProcessData(data.coverageState) ? (
                <p className="text-xs text-[var(--foreground-muted)]" data-testid="process-detail-no-data">{t('processes.noData.title')}</p>
              ) : (
                <>
                  <ProcesStappenbalk
                    bottleneck={data.bottleneck}
                    onStepSelect={(canonicalStep) => {
                      if (name === 'physical') navigate({ pathname: PHYSICAL_ITEMS_PATH, search: buildPhysicalStepSearch(canonicalStep) });
                      else navigate({ pathname: buildStepDetailPath(name, canonicalStep) });
                    }}
                    renderStepExtra={(step) => {
                      if (step.stagnation.count === 0) return null;
                      // Story 3.3: PRECIES de ene knelpunt-stap krijgt de amber "Knelpunt"-flag;
                      // elke andere stagnante stap toont alleen de neutrale stilstand-teller.
                      if (data.bottleneck?.canonicalStep === step.canonicalStep) {
                        return <BottleneckFlag countLabel={t('processes.card.stagnationValue', { count: step.stagnation.count })} label={t('processes.knelpunt.label')} />;
                      }
                      return (
                        <span className="rounded-full bg-[var(--warning-light)] px-2 py-0.5 text-[10px] font-semibold text-[var(--warning-text)]" data-testid="funnel-step-stagnation-badge">
                          {t('processes.card.stagnationValue', { count: step.stagnation.count })}
                        </span>
                      );
                    }}
                    steps={data.steps}
                  />
                  <p className="mt-3 border-t border-[var(--border)] pt-2.5 text-[11px] text-[var(--foreground-muted)]">{t('processes.detail.stepperNote')}</p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Zonder procesdata zou dit blok een leeftijd tonen onder "Nog geen procesdata"; fysiek en de bron
              onbereikbaar: geen getallen (CAP-8). */}
          {hasNoProcessData(data.coverageState) || (name === 'physical' && data.sources.procesManagement === 'unavailable') ? null : <KpiSummaryBlock kpiSummary={data.kpiSummary} />}

          {/* Brok 3 t/m 5: de producten in dit proces en de gebeurtenissenstroom. Bij een proces zonder
              procesdata is er geen bron om uit te putten — dan is de lege staat hierboven het verhaal. */}
          {hasNoProcessData(data.coverageState) ? null : (
            <Tabs defaultValue={TAB.producten} onValueChange={setTab} value={tab}>
              {/* Op 375 px met grote letters passen beide tabbladen niet naast elkaar: laat ze
                  teruglopen en het vlak meegroeien, zonder de gedeelde TabsList te veranderen. */}
              <TabsList aria-label={t('processes.detail.tabsLabel')} className="h-auto max-w-full flex-wrap">
                <TabsTrigger data-testid="proces-tab-producten" value={TAB.producten}>{t('processes.detail.productsTitle')}</TabsTrigger>
                <TabsTrigger data-testid="proces-tab-stroom" value={TAB.stroom}>{t('processes.detail.streamTitle')}</TabsTrigger>
              </TabsList>
              <TabsContent value={TAB.producten}>
                {/* Klant en drempel alleen voor het fysieke proces (story 3); de diensten houden hun lijst. */}
                <ProductenPerProces
                  filterNotApplied={listFilterNotApplied}
                  filters={name === 'physical' ? { gln: filterValues.gln, stilstandDrempelWerkdagen: filterValues.stilstandDrempelWerkdagen } : undefined}
                  ladder={data.steps}
                  name={name}
                />
              </TabsContent>
              <TabsContent value={TAB.stroom}>
                <Card data-testid="proces-stroom-kaart">
                  <CardHeader>
                    <CardTitle>{t('processes.detail.streamTitle')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <GebeurtenissenStroom actief={tab === TAB.stroom} name={name} />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}

          <div className="grid grid-cols-1 gap-4">
            <StepTransitionTimingBlock
              allCustomers={allCustomers}
              eventsSourceFetchedAt={data.sources.procesManagementEvents?.fetchedAt}
              eventsSourceUnavailable={data.sources.procesManagementEvents?.status === 'unavailable'}
              filters={filterValues}
              funnelSteps={data.steps}
              name={name}
              stepTimings={data.stepTimings}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}
